/**
 * Session Redirect URL Preservation
 *
 * Stores the user's intended destination URL when their session expires,
 * allowing them to be redirected back after re-authentication.
 */

const REDIRECT_KEY = 'dextoro_auth_redirect';
const REDIRECT_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

interface RedirectData {
  url: string;
  timestamp: number;
}

/**
 * Save the current URL for redirect after re-authentication
 */
export function saveRedirectUrl(url: string): void {
  // Don't save auth pages as redirect targets
  if (url.startsWith('/auth')) {
    return;
  }

  const data: RedirectData = {
    url,
    timestamp: Date.now(),
  };

  try {
    sessionStorage.setItem(REDIRECT_KEY, JSON.stringify(data));
  } catch (err) {
    // Silently fail if sessionStorage is unavailable (e.g., private browsing full)
    console.warn('[SessionRedirect] Failed to save redirect URL:', err);
  }
}

/**
 * Get the saved redirect URL if it exists and hasn't expired
 * Returns null if no URL is saved or if it has expired
 */
export function getRedirectUrl(): string | null {
  try {
    const raw = sessionStorage.getItem(REDIRECT_KEY);
    if (!raw) return null;

    const data: RedirectData = JSON.parse(raw);

    // Check if expired
    if (Date.now() - data.timestamp > REDIRECT_EXPIRY_MS) {
      clearRedirectUrl();
      return null;
    }

    return data.url;
  } catch {
    clearRedirectUrl();
    return null;
  }
}

/**
 * Clear the saved redirect URL
 * Should be called after successful redirect or on sign out
 */
export function clearRedirectUrl(): void {
  try {
    sessionStorage.removeItem(REDIRECT_KEY);
  } catch {
    // Silently fail
  }
}
